import { GRAPH_BASE } from "../auth/config.ts";
import { acquireToken } from "../auth/broker.ts";
import {
    withSlot,
    recordThrottle,
    recordSuccess,
    backoffDelay,
    sleep,
    type ServiceName
} from "./throttle.ts";

/**
 * Microsoft Graph client (ARCHITECTURE.md §9).
 *
 * Every request goes through the per-service concurrency budget and honours
 * Retry-After on 429 and 503. Nothing here holds response content longer than
 * the caller needs — binary bodies are handed back as streams or blobs so mail
 * and OneDrive can write straight to disk.
 */

const MAX_ATTEMPTS = 6;

/** Statuses worth retrying. 504 included: Graph returns it under load. */
const RETRYABLE = new Set([429, 503, 504]);

export class GraphError extends Error {
    readonly status: number;
    /** Graph's machine-readable code, e.g. "ErrorItemNotFound". */
    readonly code: string;
    /** Graph's request-id — the thing support asks for. */
    readonly requestId: string | null;
    readonly url: string;

    constructor(init: {
        status: number;
        code: string;
        message: string;
        requestId: string | null;
        url: string;
    }) {
        super(init.message);
        this.name = "GraphError";
        this.status = init.status;
        this.code = init.code;
        this.requestId = init.requestId;
        this.url = init.url;
    }

    /**
     * Log-safe summary. Graph error *messages* can quote resource names, so the
     * support log records the code and endpoint only (§14).
     */
    toLogString(): string {
        return `${this.status} ${this.code} ${stripQuery(this.url)}${
            this.requestId ? ` req=${this.requestId}` : ""
        }`;
    }
}

/** Drops query strings — they carry $filter/$search values derived from content. */
function stripQuery(url: string): string {
    const index = url.indexOf("?");
    return index === -1 ? url : url.slice(0, index);
}

export interface GraphRequestOptions {
    method?: string;
    /** Scopes for this call — the module's group, so consent stays incremental. */
    scopes: readonly string[];
    /** Picks the concurrency budget. Outlook endpoints are throttled harder. */
    service?: ServiceName;
    headers?: Record<string, string>;
    body?: BodyInit;
    signal?: AbortSignal;
    /** Called before each retry, so the UI can say why it is waiting. */
    onRetry?: (info: { attempt: number; delayMs: number; status: number }) => void;
}

function absolute(pathOrUrl: string): string {
    return pathOrUrl.startsWith("http") ? pathOrUrl : `${GRAPH_BASE}${pathOrUrl}`;
}

/**
 * Retry-After is seconds or an HTTP date. Graph sends seconds in practice, but
 * the date form is legal and cheap to support.
 */
function retryAfterMs(response: Response): number | null {
    const header = response.headers.get("Retry-After");
    if (!header) {
        return null;
    }

    const seconds = Number(header);
    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000);
    }

    const date = Date.parse(header);
    return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
}

async function toGraphError(response: Response, url: string): Promise<GraphError> {
    let code = String(response.status);
    let message = response.statusText;

    try {
        const body = (await response.json()) as {
            error?: { code?: string; message?: string };
        };
        code = body.error?.code ?? code;
        message = body.error?.message ?? message;
    } catch {
        // Non-JSON error body — status text is all we get.
    }

    return new GraphError({
        status: response.status,
        code,
        message,
        requestId: response.headers.get("request-id"),
        url
    });
}

/**
 * Single Graph request with throttle handling. Returns the raw Response so
 * callers can stream binaries rather than buffering them.
 *
 * Throws GraphError on a non-retryable failure, or after attempts are spent.
 * InteractionRequiredError propagates from the token broker untouched — the
 * caller pauses and checkpoints, it is not this layer's decision (§6).
 */
export async function graphFetch(
    pathOrUrl: string,
    options: GraphRequestOptions
): Promise<Response> {
    const url = absolute(pathOrUrl);
    const service = options.service ?? "default";

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        const token = await acquireToken(options.scopes);

        const response = await withSlot(service, () =>
            fetch(url, {
                method: options.method ?? "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...options.headers
                },
                ...(options.body !== undefined ? { body: options.body } : {}),
                ...(options.signal ? { signal: options.signal } : {})
            })
        );

        if (response.ok) {
            recordSuccess();
            return response;
        }

        if (!RETRYABLE.has(response.status) || attempt === MAX_ATTEMPTS - 1) {
            throw await toGraphError(response, url);
        }

        recordThrottle();

        // Retry-After is authoritative when present; back off with jitter when
        // it is absent so parallel requests do not retry in lockstep (§9).
        const delayMs = retryAfterMs(response) ?? backoffDelay(attempt);
        options.onRetry?.({ attempt: attempt + 1, delayMs, status: response.status });

        // Drain the body so the connection can be reused.
        await response.arrayBuffer().catch(() => undefined);
        await sleep(delayMs, options.signal);
    }

    // Unreachable: the loop either returns or throws.
    throw new Error("graphFetch exhausted attempts without resolving");
}

/** JSON convenience wrapper. */
export async function graphJson<T>(
    pathOrUrl: string,
    options: GraphRequestOptions
): Promise<T> {
    const response = await graphFetch(pathOrUrl, {
        ...options,
        headers: { Accept: "application/json", ...options.headers }
    });
    return (await response.json()) as T;
}
